import { DialogContent, DialogOverlay } from "@reach/dialog";
import { useWindowHeight } from "@react-hook/window-size";
import { AnimatePresence, motion } from "framer-motion";
import { atom, useSetAtom } from "jotai";
import { FC, ReactNode, useState } from "react";

import { Box, CloseIcon, IconButton, ThemeUIStyleObject } from "@ms/gatsby-theme";

import { overlayVariants } from "@theme/components/Modal/variants";

const contentVariant = {
  initial: {
    x: 100,
  },
  animate: {
    x: 0,
    transition: { min: 0, max: 100, bounceDamping: 9 },
  },
  exit: {
    x: 100,
    transition: { min: 0, max: 100, bounceDamping: 9 },
  },
};

/**
 * Flyout Cart Open Atom
 *
 * Whether the flyout cart is open
 */
export const flyoutCartOpen = atom(false);

export interface FlyoutCartModalProps {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  "aria-label": string;
  overlayStyles?: ThemeUIStyleObject;
  sx?: ThemeUIStyleObject;
  className?: string;
  children: ReactNode;
}

export interface FlyoutCartModalComponents {
  Header: FC<FlyoutCartModalHeaderProps>;
  Back: FC<FlyoutCartModalBackProps>;
  Content: FC<FlyoutCartModalContentProps>;
}

const FlyoutCartDialogOverlay = motion(DialogOverlay);
const FlyoutCartDialogContent = motion(DialogContent);

export const FlyoutCartModal: FC<FlyoutCartModalProps> & FlyoutCartModalComponents = ({
  isOpen,
  close,
  overlayStyles,
  sx,
  className,
  // unused variables but don't want them in ...rest
  open, // eslint-disable-line @typescript-eslint/no-unused-vars
  toggle, // eslint-disable-line @typescript-eslint/no-unused-vars
  children,
  ...rest
}) => {
  const height = useWindowHeight();
  const [isDoneAnimating, setIsDoneAnimating] = useState(false);

  return (
    <AnimatePresence>
      {isOpen && (
        <FlyoutCartDialogOverlay
          isOpen={isOpen}
          onDismiss={close}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={overlayVariants}
          dangerouslyBypassFocusLock={!isDoneAnimating}
          sx={{
            variant: "modals.flyoutCartOverlay",
            scrollPadding: "100px 0 200px",
            ...overlayStyles,
          }}
        >
          <FlyoutCartDialogContent
            initial="initial"
            animate="animate"
            exit="exit"
            variants={contentVariant}
            onAnimationComplete={(status) => setIsDoneAnimating(status === "animate")}
            sx={{
              variant: "modals.flyoutCartContent",
              width: "100vw !important",
              maxWidth: ["none", null, 400],
              height: "full",
              maxHeight: height,
              ...sx,
            }}
            className={className}
            {...rest}
          >
            {children}
          </FlyoutCartDialogContent>
        </FlyoutCartDialogOverlay>
      )}
    </AnimatePresence>
  );
};

interface FlyoutCartModalHeaderProps {
  sx?: ThemeUIStyleObject;
  className?: string;
  children: ReactNode;
}

export const FlyoutCartModalHeader: FC<FlyoutCartModalHeaderProps> = ({
  sx,
  className,
  children,
}) => {
  return (
    <Box
      sx={{
        bg: "goldDark",
        color: "white",
        textAlign: "center",
        py: "s500",
        position: "sticky",
        zIndex: 10,
        top: 0,
        left: 0,
        right: 0,
        ...sx,
      }}
      className={className}
    >
      {children}
    </Box>
  );
};

interface FlyoutCartModalBackProps {}

export const FlyoutCartModalBack: FC<FlyoutCartModalBackProps> = () => {
  const setIsFlyoutCartOpen = useSetAtom(flyoutCartOpen);
  return (
    <IconButton
      variant="iconDark"
      sx={{
        display: "block",
        width: "auto",
        height: "auto",
        p: "s300",
        m: 0,
        position: "absolute",
        top: "50%",
        right: "s200",
        transform: "translateY(-50%)",
      }}
      aria-label="Close cart"
      onClick={() => setIsFlyoutCartOpen(false)}
    >
      <CloseIcon sx={{ width: "iconSizeBase", height: "auto" }} />
    </IconButton>
  );
};

interface FlyoutCartModalContentProps {
  sx?: ThemeUIStyleObject;
  className?: string;
  children: ReactNode;
}

export const FlyoutCartModalContent: FC<FlyoutCartModalContentProps> = ({
  sx,
  className,
  children,
}) => {
  return (
    <Box sx={{ bg: "white", position: "relative", ...sx }} className={className}>
      {children}
    </Box>
  );
};

FlyoutCartModal.Header = FlyoutCartModalHeader;
FlyoutCartModal.Back = FlyoutCartModalBack;
FlyoutCartModal.Content = FlyoutCartModalContent;
